import { Link } from "react-router-dom";
import { Info } from "lucide-react";

/**
 * Compact, persistent affiliate disclosure banner.
 *
 * Drop this immediately above any block of monetized content (lender tables,
 * Apply CTAs, quote comparison rows). It satisfies the FTC's "clear and
 * conspicuous" standard (16 CFR Part 255) AND the standard disclosure
 * placement requirements imposed by the major auto-affiliate networks
 * (Bankrate Network, Even Financial, LendingTree, MoneyLion).
 *
 * Two visual variants:
 *   - "inline" (default) — light pill above the section, blends with UI
 *   - "banner" — full-width strip with stronger emphasis, for primary tables
 */
export default function AffiliateDisclosure({
  variant = "inline",
  className = "",
  children,
}) {
  // Default copy is intentionally short and direct so it doesn't add visual
  // clutter — readers see "Advertiser disclosure" + a hyperlink to the full
  // policy at /disclosure on a single line.
  const copy = children || (
    <>
      <strong className="text-slate-950 font-bold">Advertiser disclosure:</strong>{" "}
      Offers below are from partners that compensate us when you click or apply.
      Compensation does not determine our rankings.{" "}
      <Link
        to="/disclosure"
        className="underline hover:text-slate-950 transition-colors"
      >
        How we make money
      </Link>
      .
    </>
  );

  if (variant === "banner") {
    return (
      <div
        data-testid="affiliate-disclosure-banner"
        className={`flex items-start gap-2.5 border border-slate-200 bg-[#FAFAFA] px-4 py-3 text-xs text-slate-600 leading-relaxed ${className}`}
      >
        <Info className="w-3.5 h-3.5 text-slate-500 mt-0.5 shrink-0" aria-hidden />
        <div>{copy}</div>
      </div>
    );
  }
  return (
    <p
      data-testid="affiliate-disclosure-inline"
      className={`inline-flex items-start gap-1.5 text-[11px] text-slate-500 leading-relaxed ${className}`}
    >
      <Info className="w-3 h-3 text-slate-500 mt-0.5 shrink-0" aria-hidden />
      <span>{copy}</span>
    </p>
  );
}
